import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronDown, LayoutDashboard, LogOut, UserRound } from "lucide-react";

export const ATHLETE_TOKEN_KEY = "fg_athlete_token";
const AUTH_CHANGED_EVENT = "fg-athlete-auth-changed";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const notifyAthleteAuthChanged = () => {
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
};

export const signOutAthlete = () => {
  localStorage.removeItem(ATHLETE_TOKEN_KEY);
  notifyAthleteAuthChanged();
};

export const useAthleteAuth = () => {
  const [signedIn, setSignedIn] = useState(() => Boolean(localStorage.getItem(ATHLETE_TOKEN_KEY)));

  useEffect(() => {
    const sync = () => setSignedIn(Boolean(localStorage.getItem(ATHLETE_TOKEN_KEY)));
    window.addEventListener("storage", sync);
    window.addEventListener(AUTH_CHANGED_EVENT, sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener(AUTH_CHANGED_EVENT, sync);
    };
  }, []);

  return signedIn;
};

export function SignupLink({ className = "", testId = "nav-link-signup", onNavigate = () => {}, children = "Sign Up" }) {
  const signedIn = useAthleteAuth();
  if (signedIn) return null;
  return (
    <Link to="/signup" onClick={onNavigate} data-testid={testId} className={className}>
      {children}
    </Link>
  );
}

function Avatar({ name, photoUrl, testId = "account-avatar" }) {
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  return (
    <span className="w-9 h-9 rounded-full overflow-hidden border border-[#0D1117]/10 bg-[#FBF9F5] flex items-center justify-center shrink-0" data-testid={testId}>
      {photoUrl ? (
        <img src={photoUrl} alt={name ? `${name} profile photo` : "Athlete profile photo"} className="w-full h-full object-cover" />
      ) : initials ? (
        <span className="font-mono text-[10px] tracking-[0.1em] text-[#FF4E00]">{initials}</span>
      ) : (
        <UserRound className="w-4 h-4 text-[#6B7280]" />
      )}
    </span>
  );
}

export default function AccountMenu({ mobile = false, onNavigate = () => {} }) {
  const signedIn = useAthleteAuth();
  const [profile, setProfile] = useState({ name: "", photoUrl: "" });
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!signedIn) {
      setProfile({ name: "", photoUrl: "" });
      return undefined;
    }
    let active = true;
    let photoUrl = "";
    const token = localStorage.getItem(ATHLETE_TOKEN_KEY);
    const load = async () => {
      try {
        const response = await axios.get(`${API}/athlete/profile`, { headers: { Authorization: `Bearer ${token}` } });
        if (!active) return;
        let nextPhotoUrl = "";
        if (response.data.photo_path) {
          try {
            const photo = await axios.get(`${API}/athlete/profile/photo`, {
              headers: { Authorization: `Bearer ${token}` },
              responseType: "blob",
            });
            photoUrl = URL.createObjectURL(photo.data);
            nextPhotoUrl = photoUrl;
          } catch {
            nextPhotoUrl = "";
          }
        }
        if (active) setProfile({ name: response.data.name || "", photoUrl: nextPhotoUrl });
      } catch (error) {
        if (error?.response?.status === 401) signOutAthlete();
      }
    };
    load();
    return () => {
      active = false;
      if (photoUrl) URL.revokeObjectURL(photoUrl);
    };
  }, [signedIn]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const signOut = () => {
    signOutAthlete();
    setOpen(false);
    onNavigate();
    navigate("/profile");
  };

  const active = location.pathname === "/profile";
  const firstName = profile.name.split(" ")[0] || "Account";
  const iconButtonCls = `w-9 h-9 rounded-full border flex items-center justify-center transition-all duration-300 ${
    active
      ? "bg-[#FF4E00] border-[#FF4E00] text-[#FBF9F5]"
      : "border-[#0D1117]/15 text-[#374151] hover:border-[#FF4E00] hover:text-[#FF4E00]"
  }`;

  if (mobile) {
    if (!signedIn) {
      return (
        <Link to="/profile" onClick={onNavigate} data-testid="nav-mobile-profile" className="font-mono text-xs uppercase tracking-[0.2em] text-[#374151] hover:text-[#FF4E00] transition-colors flex items-center gap-2">
          <UserRound className="w-3.5 h-3.5" /> Sign In
        </Link>
      );
    }
    return (
      <div className="flex flex-col gap-5" data-testid="nav-mobile-account-menu">
        <div className="flex items-center gap-3">
          <Avatar name={profile.name} photoUrl={profile.photoUrl} testId="nav-mobile-account-avatar" />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#0D1117]" data-testid="nav-mobile-account-name">{firstName}</span>
        </div>
        <Link to="/profile" onClick={onNavigate} data-testid="nav-mobile-profile" className="font-mono text-xs uppercase tracking-[0.2em] text-[#374151] hover:text-[#FF4E00] transition-colors flex items-center gap-2">
          <LayoutDashboard className="w-3.5 h-3.5" /> Dashboard
        </Link>
        <button type="button" onClick={signOut} data-testid="nav-mobile-sign-out" className="font-mono text-xs uppercase tracking-[0.2em] text-left text-[#6B7280] hover:text-[#FF4E00] transition-colors flex items-center gap-2">
          <LogOut className="w-3.5 h-3.5" /> Sign Out
        </button>
      </div>
    );
  }

  if (!signedIn) {
    return (
      <Link to="/profile" data-testid="nav-link-profile" aria-label="Sign In" title="Sign In" className={iconButtonCls}>
        <UserRound className="w-4 h-4" />
      </Link>
    );
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="account-menu-button"
        aria-label="Account menu"
        aria-expanded={open}
        className={`h-10 rounded-full border flex items-center gap-2 pl-0.5 pr-2 sm:pr-3 transition-all duration-300 ${
          active
            ? "bg-[#FF4E00] border-[#FF4E00] text-[#FBF9F5]"
            : "border-[#0D1117]/15 text-[#374151] hover:border-[#FF4E00] hover:text-[#FF4E00]"
        }`}
      >
        <Avatar name={profile.name} photoUrl={profile.photoUrl} />
        <span className="hidden xl:inline font-mono text-[10px] uppercase tracking-[0.14em] max-w-28 truncate" data-testid="account-menu-name">{firstName}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-12 w-56 rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] shadow-xl p-2 z-50" data-testid="account-menu">
          <div className="px-4 py-3 border-b border-[#0D1117]/10 mb-2">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#6B7280]">Signed in as</p>
            <p className="text-sm font-medium text-[#0D1117] truncate mt-1" data-testid="account-menu-full-name">{profile.name || "Athlete"}</p>
          </div>
          <Link
            to="/profile"
            onClick={() => setOpen(false)}
            data-testid="account-menu-profile"
            className="flex items-center gap-3 rounded-xl px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[#374151] hover:bg-[#FBF9F5] hover:text-[#FF4E00] transition-colors"
          >
            <LayoutDashboard className="w-3.5 h-3.5" /> Dashboard
          </Link>
          <button
            type="button"
            onClick={signOut}
            data-testid="account-menu-sign-out"
            className="w-full flex items-center gap-3 rounded-xl px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[#6B7280] hover:bg-[#FBF9F5] hover:text-[#FF4E00] transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>
      )}
    </div>
  );
}
